/**
 * Google reCAPTCHA v2 helpers for Create account.
 */

const SCRIPT_ID = 'craftara-recaptcha-v2';
const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit';

export function getRecaptchaSiteKey(): string {
  return (import.meta.env.VITE_RECAPTCHA_SITE_KEY || '').trim();
}

declare global {
  interface Window {
    grecaptcha?: {
      ready: (cb: () => void) => void;
      render: (
        container: HTMLElement | string,
        parameters: {
          sitekey: string;
          theme?: 'light' | 'dark';
          size?: 'normal' | 'compact';
          callback?: (token: string) => void;
          'expired-callback'?: () => void;
          'error-callback'?: () => void;
        }
      ) => number;
      getResponse: (widgetId?: number) => string;
      reset: (widgetId?: number) => void;
    };
  }
}

let scriptPromise: Promise<void> | null = null;

export function loadRecaptchaScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('reCAPTCHA requires a browser'));
  }
  if (window.grecaptcha?.render) {
    return Promise.resolve();
  }
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () =>
        reject(new Error('Failed to load reCAPTCHA'))
      );
      if (window.grecaptcha?.render) resolve();
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Failed to load reCAPTCHA'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

export async function renderRecaptcha(
  container: HTMLElement,
  options: {
    onChange?: (token: string) => void;
    onExpired?: () => void;
    onError?: () => void;
  } = {}
): Promise<number> {
  const siteKey = getRecaptchaSiteKey();
  if (!siteKey) {
    throw new Error(
      'reCAPTCHA site key is missing. Set VITE_RECAPTCHA_SITE_KEY in apps/web/.env.local.'
    );
  }

  await loadRecaptchaScript();
  const grecaptcha = window.grecaptcha;
  if (!grecaptcha) {
    throw new Error('reCAPTCHA failed to initialize');
  }

  return new Promise((resolve, reject) => {
    grecaptcha.ready(() => {
      try {
        container.innerHTML = '';
        const widgetId = grecaptcha.render(container, {
          sitekey: siteKey,
          callback: (token) => options.onChange?.(token),
          'expired-callback': () => options.onExpired?.(),
          'error-callback': () => options.onError?.(),
        });
        resolve(widgetId);
      } catch (err) {
        reject(err instanceof Error ? err : new Error('Failed to render reCAPTCHA'));
      }
    });
  });
}

export function getRecaptchaResponse(widgetId: number | null): string {
  if (widgetId === null || !window.grecaptcha) return '';
  return window.grecaptcha.getResponse(widgetId) || '';
}

export function resetRecaptcha(widgetId: number | null): void {
  if (widgetId === null || !window.grecaptcha) return;
  window.grecaptcha.reset(widgetId);
}
